import React, { useState } from "react";
import { useContactMutation } from "../core/Features/api/contact/contact";
import toast, { Toaster } from "react-hot-toast";
import { motion } from "framer-motion";

interface FormData {
  name: string;
  email: string;
  subject: string;
  message: string;
}

const initialFormData: FormData = {
  name: "",
  email: "",
  subject: "",
  message: "",
};

const Contact = () => {
  const [submitContact] = useContactMutation();
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = () => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};
    if (!formData.name.trim()) newErrors.name = "Name is required.";
    if (!formData.email.trim()) {
      newErrors.email = "Email is required.";
    } else if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
      newErrors.email = "Invalid email address.";
    }
    if (!formData.subject.trim()) newErrors.subject = "Subject is required.";
    if (!formData.message.trim()) newErrors.message = "Message is required.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);

    try {
      await submitContact(formData).unwrap();
      toast.success("Contact Form submitted successfully!");
      setFormData(initialFormData);
      setErrors({});
    } catch (error: any) {
      toast.error(error?.message || "Something went wrong.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
<div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-12 px-6">
      <Toaster position="top-center" />

      <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-6 ">
        {/* Contact Form */}
      <motion.div
  initial={{ boxShadow: "0 0 0 rgba(255,0,0,0.4)" }}
  whileHover={{
    boxShadow: [
      "0 0 10px rgba(255,0,0,0.5)",
      "0 0 25px rgba(255,0,0,0.8)",
      "0 0 10px rgba(255,0,0,0.5)"
    ]
  }}
  transition={{
    duration: 3,
    ease: "easeInOut",
    repeat: Infinity,
  }}
  className="bg-white rounded-2xl p-8 border border-gray-200"
>
          <h2 className="text-3xl font-bold text-center pb-2 text-[#1D1753]">
            Get in Touch
          </h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-gray-600 font-semibold mb-1">Name</label>
                <input
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  className="w-full bg-gray-50 px-4 py-3 border-1 border-gray-300 rounded-xl shadow-sm 
             focus:outline-none focus-within:border-indigo-500 
             transition-all duration-300"                />
                {errors.name && <div className="text-red-600 text-sm mt-1">{errors.name}</div>}
              </div>

              <div>
                <label className="block text-gray-600 font-semibold mb-1">Email</label>
                <input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                   className="w-full bg-gray-50 px-4 py-3 border-1 border-gray-300 rounded-xl shadow-sm 
             focus:outline-none focus-within:border-indigo-500 
             transition-all duration-300"                />
                {errors.email && <div className="text-red-600 text-sm mt-1">{errors.email}</div>}
              </div>
            </div>

            <div>
              <label className="block text-gray-600 font-semibold mb-1">Subject</label>
              <input
                name="subject"
                type="text"
                value={formData.subject}
                onChange={(e) => handleChange("subject", e.target.value)}
             className="w-full bg-gray-50 px-4 py-3 border-1 border-gray-300 rounded-xl shadow-sm 
             focus:outline-none focus-within:border-indigo-500 
             transition-all duration-300"             />
              {errors.subject && <div className="text-red-600 text-sm mt-1">{errors.subject}</div>}
            </div>

            <div>
              <label className="block text-gray-600 font-semibold mb-1">Message</label>
              <textarea
                name="message"
                rows={5}
                value={formData.message}
                onChange={(e) => handleChange("message", e.target.value)}
                className="w-full bg-gray-50 px-4 py-3 border-1 border-gray-300 rounded-xl shadow-sm 
             focus:outline-none focus-within:border-indigo-500 
             transition-all duration-300"            ></textarea>
              {errors.message && <div className="text-red-600 text-sm mt-1">{errors.message}</div>}
            </div>

            <div className="text-center">
              <button
                type="submit"
                disabled={isSubmitting}
                className={`px-8 py-3 rounded-xl font-semibold shadow-md transition-all duration-300
    ${isSubmitting
                    ? "bg-gray-400 text-gray-200 cursor-not-allowed"
                    : "bg-[#ff0000] text-white"}`}
              >
                {isSubmitting ? "Submitting..." : "Send Message"}
              </button>

            </div>
          </form>
        </motion.div>

        {/* Google Map */}
        <motion.div
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="rounded-2xl overflow-hidden shadow-2xl border border-gray-200"
        >
          <iframe
            className="w-full h-full min-h-[400px]"
            title="map"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3916.3585525205117!2d76.98215027409064!3d11.011702054811886!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ba859f421d692bd%3A0x84e786e94abf0a83!2sPalanivel%20Overseas%20Education%20-%20Study%20Abroad%20Consultants%20in%20Coimbatore!5e0!3m2!1sen!2sin!4v1753869172290!5m2!1sen!2sin"

            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
          ></iframe>
        </motion.div>
      </div>
    </div>
  );
};

export default Contact;
